import type { Permission_index } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import {
  getDecodedWeaveToken,
  getUser,
  getWeaveToken,
  hasACL,
  isWeaveTokenActive,
  clearStoredAuthData,
} from '@frontend/auth-helpers';
import { createStore, createShallowStore } from '@frontend/store';
import { isDevBackend } from '../constants';

/** AIR Trainer ACL permission indexes from waccess schema */
export const AIR_TRAINER_ACLS = {
  READ: 1801 as Permission_index,
  MANAGE: 1802 as Permission_index,
  ADMIN: 1803 as Permission_index,
} as const;

/**
 * In dev environments (weavedev.net backend), skip ACL validation so developers
 * can test without needing the AIR Trainer ACLs provisioned on their accounts.
 * In prod, ACL checks are always enforced.
 */
const SKIP_ACL_IN_DEV = isDevBackend();

/**
 * Checks whether the current user has at least one of the required AIR Trainer ACLs.
 * Uses the same `hasACL` helper from @frontend/auth-helpers that the weave app uses.
 */
const checkAirTrainerAcl = (): boolean => {
  if (SKIP_ACL_IN_DEV) return true;

  const hasRead = hasACL<Permission_index>('weave', AIR_TRAINER_ACLS.READ);
  const hasManage = hasACL<Permission_index>('weave', AIR_TRAINER_ACLS.MANAGE);
  const hasAdmin = hasACL<Permission_index>('weave', AIR_TRAINER_ACLS.ADMIN);

  return !!hasRead || !!hasManage || !!hasAdmin;
};

/**
 * Derive a display-friendly first/last name from an email address.
 * Handles formats like "sammy.madasamy@..." → ("Sammy", "Madasamy")
 */
const deriveNameFromEmail = (email: string): { first: string; last: string } => {
  const localPart = email.split('@')[0] ?? '';
  const parts = localPart.split(/[._-]/);
  const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');
  return {
    first: capitalize(parts[0] ?? ''),
    last: capitalize(parts[1] ?? ''),
  };
};

interface AuthStoreState {
  isAuthenticated: boolean;
  hasRequiredAcl: boolean;
  firstName: string;
  lastName: string;
  email: string;
  /** The user's UUID — used as `trainerId` for HITL API calls. */
  userId: string;

  /** Hydrate auth state from localStorage token + user data. */
  hydrate: () => void;
  /** Clear all auth data and reset store. */
  logout: () => void;
}

export const useAuthStore = createStore<AuthStoreState>(
  (set) => ({
    isAuthenticated: false,
    hasRequiredAcl: false,
    firstName: '',
    lastName: '',
    email: '',
    userId: '',

    hydrate: () => {
      const token = getWeaveToken();
      const authenticated = !!token && isWeaveTokenActive();

      if (!authenticated) {
        set({ isAuthenticated: false, hasRequiredAcl: false, firstName: '', lastName: '', email: '', userId: '' });
        return;
      }

      const decoded = getDecodedWeaveToken<Permission_index>();
      const email = decoded?.username ?? '';
      const userId = decoded?.user_id ?? '';

      // Try getting user details from localStorage (stored by authnClient.fetchUser() → setUser())
      const user = getUser();
      let firstName = user?.firstName ?? '';
      let lastName = user?.lastName ?? '';

      // Fallback: if PortalUser doesn't have names, derive from the email
      if (!firstName && email) {
        const derived = deriveNameFromEmail(email);
        firstName = derived.first;
        lastName = derived.last;
      }

      set({
        isAuthenticated: true,
        hasRequiredAcl: checkAirTrainerAcl(),
        firstName,
        lastName,
        email,
        userId,
      });
    },

    logout: () => {
      clearStoredAuthData();
      set({ isAuthenticated: false, hasRequiredAcl: false, firstName: '', lastName: '', email: '', userId: '' });
    },
  }),
  { name: 'air-trainer-auth-store' }
);

export const useAuthShallowStore = createShallowStore<AuthStoreState>(useAuthStore);
